'use client'

import { Typography } from '../typography'
import { BaseCard } from './base-card'
import { CodeExample } from '../demo/code-example'
import { DemoTabs } from '../demo/demo-tabs'

function Placeholder() {
  return (
    <div className="flex h-[90px] w-full items-center justify-center rounded border border-dashed border-cf-blue-700 bg-cf-blue-50">
      <span className="text-14/5 font-medium text-cf-grey-1000">Placeholder</span>
    </div>
  )
}

export function BaseCardDemo() {
  return (
    <div className="space-y-2">
      <Typography variant="cf-heading-01" className="text-cf-grey-900">
        Base Card
      </Typography>

      <DemoTabs
        tabs={{
          preview: (
            <div className="space-y-6">
              {/* Sizes */}
              <div className="border-cf-grey-100 rounded-lg border p-6">
                <Typography variant="cf-heading-02" className="text-cf-grey-900 mb-4">
                  Sizes
                </Typography>
                <div className="space-y-4">
                  <div>
                    <div className="grid grid-cols-2 gap-8">
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Sm
                        </Typography>
                        <Typography variant="body-01" className="text-cf-grey-600 mb-2">
                          Used for dense UIs, dashboards, or list items where space is limited.
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard size="sm" className="w-[320px]">
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Md (default)
                        </Typography>
                        <Typography variant="body-01" className="text-cf-grey-600 mb-2">
                          Used for standard display, marketing pages, or detail views.
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard size="md" className="w-[320px]">
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                    </div>
                    <CodeExample
                      code={`import { BaseCard } from '@repo/design-system-cf/components/base-card'

// Small size (16px padding)
<BaseCard size="sm">
  <YourContent />
</BaseCard>

// Medium size (24px padding) - default
<BaseCard size="md">
  <YourContent />
</BaseCard>`}
                    />
                  </div>
                </div>
              </div>

              {/* Appearances */}
              <div className="border-cf-grey-100 rounded-lg border p-6">
                <Typography variant="cf-heading-02" className="text-cf-grey-900 mb-4">
                  Appearances
                </Typography>
                <div className="space-y-4">
                  <div>
                    <div className="grid grid-cols-3 gap-8">
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Default
                        </Typography>
                        <Typography variant="body-01" className="text-cf-grey-600 mb-2">
                          Used on colored backgrounds (e.g. grey-50) to provide maximum contrast.
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard appearance="default" className="w-[320px]">
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Inverse
                        </Typography>
                        <Typography variant="body-01" className="text-cf-grey-600 mb-2">
                          Used when the page background is white. Light grey provides subtle
                          contrast.
                        </Typography>
                        <div className="rounded border border-cf-grey-200 bg-cf-grey-0 px-4 py-8 flex justify-center items-center">
                          <BaseCard appearance="inverse" className="w-[320px]">
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Custom
                        </Typography>
                        <Typography variant="body-01" className="text-cf-grey-600 mb-2">
                          Custom appearance can be created by passing a custom background color to
                          the className prop.
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard className="w-[320px] bg-cf-purple-50">
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                    </div>
                    <CodeExample
                      code={`import { BaseCard } from '@repo/design-system-cf/components/base-card'

// Default appearance (white background)
// Use on colored page backgrounds
<BaseCard appearance="default">
  <YourContent />
</BaseCard>

// Inverse appearance (grey-50 background)
// Use on white page backgrounds
<BaseCard appearance="inverse">
  <YourContent />
</BaseCard>

// Custom appearance (purple-50 background)
<BaseCard className="bg-cf-purple-50">
  <YourContent />
</BaseCard>`}
                    />
                  </div>
                </div>
              </div>

              {/* Interactive */}
              <div className="border-cf-grey-100 rounded-lg border p-6">
                <Typography variant="cf-heading-02" className="text-cf-grey-900 mb-4">
                  Interactive
                </Typography>
                <Typography variant="body-01" className="text-cf-grey-600 mb-4">
                  Cards may have interaction states when the entire card area is clickable or
                  hoverable. Interactive cards show hover elevation and focus ring states.
                </Typography>
                <div className="space-y-4">
                  <div>
                    <div className="grid grid-cols-2 gap-8">
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          Interactive card
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard
                            interactive
                            className="w-[320px]"
                            role="button"
                            // oxlint-disable-next-line no-alert
                            onClick={() => alert('Interactive card clicked')}
                          >
                            <Placeholder />
                          </BaseCard>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Typography variant="cf-heading-03" className="text-cf-grey-800">
                          As link (asChild)
                        </Typography>
                        <div className="rounded bg-cf-grey-100 px-4 py-8 flex justify-center items-center">
                          <BaseCard interactive asChild className="w-[320px]">
                            <a href="#example">
                              <Placeholder />
                            </a>
                          </BaseCard>
                        </div>
                      </div>
                    </div>
                    <CodeExample
                      code={`import { BaseCard } from '@repo/design-system-cf/components/base-card'

// Interactive card as button
<BaseCard 
  interactive 
  role="button"
  onClick={() => alert('Interactive card clicked')}
>
  <YourContent />
</BaseCard>

// Interactive card as link using asChild
<BaseCard interactive asChild>
  <a href="/destination">
    <YourContent />
  </a>
</BaseCard>

// With Next.js Link
import Link from 'next/link'

<BaseCard interactive asChild>
  <Link href="/destination">
    <YourContent />
  </Link>
</BaseCard>`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ),
          api: {
            properties: [
              {
                name: 'size',
                type: "'sm' | 'md'",
                defaultValue: "'md'",
                description:
                  'The size of the card. "sm" uses 16px padding, "md" uses 24px padding.',
              },
              {
                name: 'appearance',
                type: "'default' | 'inverse'",
                defaultValue: "'default'",
                description:
                  'The visual appearance. "default" has white background for use on colored backgrounds. "inverse" has grey-50 background for use on white backgrounds.',
              },
              {
                name: 'interactive',
                type: 'boolean',
                defaultValue: 'false',
                description:
                  'Whether the card is interactive (clickable/hoverable). Adds hover shadow, border changes, and focus ring.',
              },
              {
                name: 'asChild',
                type: 'boolean',
                defaultValue: 'false',
                description:
                  'Whether to merge props onto the child instead of creating a new element. Useful for rendering as a link or other element.',
              },
              {
                name: 'className',
                type: 'string',
                defaultValue: 'undefined',
                description: 'Additional CSS classes to apply.',
              },
              {
                name: 'children',
                type: 'ReactNode',
                defaultValue: 'undefined',
                description: 'The content to render inside the card.',
              },
            ],
          },
          changelog: [
            {
              date: '01-12-2025',
              changes: [
                'Initial version with size (sm/md), appearance (default/inverse), interactive mode, and asChild support.',
              ],
            },
          ],
          design: (
            <iframe
              style={{ border: '1px solid rgba(0, 0, 0, 0.1)' }}
              width="100%"
              height="100%"
              src="https://embed.figma.com/proto/c8oXlEODLmj3csp3ux5DKu/Design-System---Components?node-id=2386-1892&m=dev&scaling=min-zoom&content-scaling=fixed&embed-host=share"
              allowFullScreen
            />
          ),
        }}
      />
    </div>
  )
}
